import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import Toggle from 'react-toggle'
import FontAwesomeIcon from '@fortawesome/react-fontawesome'
import {
  faLock,
  faTh,
  faBookmark,
  faDoorOpen,
  faThLarge,
  faThList,
  faBars,
  faTimes,
  faGavel
} from '@fortawesome/fontawesome-free-solid'

import moment from 'moment-timezone'

import Api from '../../services/api'
import * as Socket from '../../services/socket'
import { showModal } from '../../actions'
import LotInfo from '../AuctionLive/LotInfo/LotInfoContainer'
import WatchButton from '../WatchButton/WatchButtonContainer'
import { makePrice } from '../../utils/Lots'
import { getInputPlaceholder } from '../../utils/Bidding'
import { offerHistory, offerPartyLabel } from '../../utils/OfferBoard'

/*
 * OfferBoard
 * ----------
 * The Bid & Offer Board screen. The board and its listings come from the API
 * (`/offer-boards/:number` and `/offer-boards/:id/lots`), which serves the
 * published listings only - what is on this screen is what is on the board.
 *
 * It reuses the live auction screen's own classNames (live-app-header,
 * live-navbar, render-lots-container, lot-item, bidding-container ...) so the
 * shared compiled App.css styles it, with the bidding controls swapped for the
 * offer flow.
 *
 * The offers have no endpoints yet: an offer a Buyer makes lives in this screen
 * for the session and nothing reaches the API, and a Seller's Offers Received
 * list stays empty until those endpoints exist. No offer is invented here.
 */

// An offer is made in the same money the listing is priced in, the way a bid
// is: cents per kilo on a ¢/kg listing, dollars per head on a $/head one. So
// what is typed in is read as cents on the first and as dollars on the second,
// and it is shown back in the same unit it was made in.
const isPerKg = (lot) => Boolean(lot) && lot.bidding === 'kg'
const fmt = (lot, cents) => makePrice(Number(cents), { bidding: lot && lot.bidding })
const toCents = (lot, amount) => {
  const value = parseFloat(amount)
  return isPerKg(lot) ? value : Math.round(value * 100)
}

const OPEN_STATUSES = ['pending', 'countered']

// Mirrors the server: the amount on the table is the Buyer's offer until one of
// them counters, and from there the last counter of either side. Whoever did
// not put it there is the one the offer is waiting on, so the two of them can
// counter each other for as long as they want.
const amountOnTheTable = (offer) => offer.status === 'countered' ? offer.counterCents : offer.cents
const awaitingParty = (offer) => {
  if (!offer || offer.status !== 'countered') {
    return 'seller'
  }
  // a counter from before the Buyer could counter back carries no side - it was
  // the Seller's
  return offer.counterParty === 'buyer' ? 'seller' : 'buyer'
}

// An auction runs to a single Sydney time, so the live screen shows Sydney.
// The board just sits there open, so its times are shown in the timezone of
// whoever is looking - Sydney in Australia, IST in India, and so on.
const localTime = (date, format) => moment(date).tz(moment.tz.guess()).format(format)

// A listing as this screen shows it
const mapLot = (lot) => {
  const details = lot.publicDetails || {}
  const location = details.location || {}
  const weight = details.weight || {}
  const summary = details.summary || {}
  const media = Array.isArray(lot.media) ? lot.media.find(one => one && (one.thumbnail || one.url)) : null
  const state = (Array.isArray(location.state) ? location.state : [location.state]).filter(Boolean).join('/')
  return {
    id: lot._id,
    createdBy: lot.createdBy,
    number: lot.number || lot.searchNumericId || '-',
    image: media ? (media.thumbnail || media.url) : null,
    // how the listing is priced, and so how an offer on it is made
    bidding: lot.bidding,
    price: makePrice(lot.startPrice, { bidding: lot.bidding }),
    priceMeasure: lot.bidding === 'kg' ? '/kg' : '/head',
    location: `${location.town || ''}${location.town && state ? ', ' : ''}${state}`.toUpperCase(),
    title: details.title || lot.description || 'Lot',
    age: `${summary.age || ''} ${lot.additionalTitle || ''}`.trim(),
    assWeight: weight.average ? `${Math.round(weight.average)}kg ass/wt` : '',
    curWeight: weight.curfew ? `${Math.round(weight.curfew)}kg cur/wt` : '',
    daysLeft: lot.daysLeft,
    expiresAt: lot.expiresAt,
    sold: lot.status === 'sold',
    soldAt: lot.soldAt,
    soldPrice: lot.closedPrice ? makePrice(lot.closedPrice, { bidding: lot.bidding }) : '',
    winner: (lot.winner && (lot.winner._id || lot.winner)) || null
  }
}

// A sale stays on the My Offers tab of both sides for a week after it happens,
// so a Seller and his Buyer keep seeing it after it comes off the board.
const SOLD_ON_SCREEN_DAYS = 7
// and it is held on the All tab for a day, so whoever was offering on a listing
// sees that it sold instead of finding it simply gone
const SOLD_ON_BOARD_HOURS = 24

// The board reads by Lot number, so a sale slotted back in among the open
// listings goes to its own number - not to the end of the list. A listing with
// no number of its own sorts last, keeping the order it arrived in.
const byLotNumber = (one, other) => {
  let first = parseInt(one.number, 10)
  let second = parseInt(other.number, 10)
  if (isNaN(first) && isNaN(second)) {
    return 0
  }
  if (isNaN(first)) {
    return 1
  }
  if (isNaN(second)) {
    return -1
  }
  return first - second
}

const EMPTY_TAB_NOTE = {
  all: 'No listings on the board yet.',
  watching: 'You are not watching any listing on this board.',
  myOffers: 'You have no offers and no listings on this board.',
  sold: `Nothing has been sold from this board in the last ${SOLD_ON_SCREEN_DAYS} days.`
}

// what the popup says when the board has been archived and the API did not say
// it in its own words
const ARCHIVED_NOTE = 'This Offer Board has been archived. You can no longer make offers on it.'

class OfferBoard extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      offerBoard: null,
      lots: [],
      loaded: false,
      loadError: '',
      unavailable: false,
      tab: 'all',
      gridState: 'default',
      isSound: true,
      selectedLotId: null,
      showDetailsPopup: false,
      offerUnlocked: false,
      offerInput: '',
      offers: [], 
      soldLots: [], // what the board sold recently, it is off the board itself
      counterFor: null, // offerId the Seller is countering
      counterInput: ''
    }
    // the details panel, so a click landing outside of it can close it
    this.panelRef = React.createRef()
  }

  async componentDidMount () {
    document.addEventListener('mousedown', this.onDocumentMouseDown)
    let number = this.props.match.params.offerBoardNumber
    // read silently, so an archived board is told to the user in the popup
    // below instead of the standard error one
    let res = await Api.getOfferBoard(number, { silent: true })
    if (res && res.isError) {
      this.boardUnavailable(res.message)
      return
    }
    if (!res || !res.offerBoard) {
      this.setState({ loaded: true, loadError: 'This Offer Board is not available.' })
      return
    }
    let lotsRes = await Api.getOfferBoardLots(res.offerBoard._id)
    let lots = ((lotsRes && lotsRes.lots) || []).map(mapLot)
    // nothing is selected until the user picks a lot
    this.setState({
      offerBoard: res.offerBoard,
      lots,
      loaded: true
    }, () => {
      this.loadOffers()
      this.loadSoldLots()
      this.subscribeToBoard()
    })
  }

  // Live updates: join this board's socket room and refresh lots + offers
  // whenever any offer changes (created / accepted / declined / countered /
  // withdrawn) - no manual refresh, mirrors the admin offers screen.
  subscribeToBoard = () => {
    let boardId = this.state.offerBoard && this.state.offerBoard._id
    if (!boardId) {
      return
    }
    Socket.connect(Socket.OFFER_BOARD_NAMESPACE, () => {
      Socket.joinOfferBoard(boardId)
      Socket.attach(
        Socket.OFFER_BOARD_NAMESPACE,
        'offer-update',
        'offer-board',
        this.onOfferUpdate
      )
    })
  }

  // The board is finished with, so there is nothing left on this screen to look
  // at or to offer on: it is emptied and the popup says what happened. It is
  // reached either by opening the link once the board is archived, or by the
  // board being archived while this screen was left open.
  boardUnavailable = (message) => {
    if (this.state.unavailable) {
      return
    }
    this.setState({ loaded: true, loadError: '', unavailable: true, selectedLotId: null })
    this.props.showModal({
      title: 'Offer Board Archived',
      message: message || ARCHIVED_NOTE
    })
  }

  onOfferUpdate = (data) => {
    // the admin has archived the board while this screen was open
    if (data && data.event === 'archived') {
      this.boardUnavailable()
      return
    }
    this.reloadLots()
    this.loadOffers()
    // an accepted offer sells a listing, which takes it off the board and puts
    // it among the sales
    this.loadSoldLots()
  }

  componentWillUnmount () {
    document.removeEventListener('mousedown', this.onDocumentMouseDown)
    let boardId = this.state.offerBoard && this.state.offerBoard._id
    Socket.detach(Socket.OFFER_BOARD_NAMESPACE, 'offer-update', 'offer-board')
    if (boardId) {
      Socket.leaveOfferBoard(boardId)
    }
  }

  // the offers this user made and the offers made on his own listings
  loadOffers = async () => {
    if (!this.state.offerBoard || !this.props.loggedIn) {
      return
    }
    let res = await Api.getOfferBoardOffers(this.state.offerBoard._id)
    if (res && res.offers) {
      this.setState({ offers: res.offers })
    }
  }

  // What the board sold in the last week. A sale comes off the board itself the
  // moment it happens, this is what keeps it on screen after.
  loadSoldLots = async () => {
    if (!this.state.offerBoard) {
      return
    }
    let res = await Api.getOfferBoardLots(this.state.offerBoard._id, {
      listing: 'sold',
      soldWithinDays: SOLD_ON_SCREEN_DAYS
    })
    if (res && res.lots) {
      this.setState({ soldLots: res.lots.map(mapLot) })
    }
  }

  // what the board sold in the last day - these stay on the All tab, the rest of
  // the week's sales are found on the Sold tab
  recentlySold = () => {
    let since = moment().subtract(SOLD_ON_BOARD_HOURS, 'hours')
    return this.state.soldLots.filter(one => one.soldAt && moment(one.soldAt).isAfter(since))
  }

  // the sales this user was part of - his own listings, and what he bought
  mySoldLots = () => {
    let userId = this.props.user && this.props.user._id
    if (!userId) {
      return []
    }
    return this.state.soldLots.filter(one => {
      return this.isOwnLot(one) || String(one.winner) === String(userId)
    })
  }

  reloadLots = async () => {
    if (!this.state.offerBoard) {
      return
    }
    let res = await Api.getOfferBoardLots(this.state.offerBoard._id)
    if (res && res.lots) {
      this.setState({ lots: res.lots.map(mapLot) })
    }
  }

  // the offer this user has open on a listing, if any
  myOffer = (lotId) => {
    let userId = this.props.user && this.props.user._id
    return this.state.offers.find(one => {
      return String(one.lot) === String(lotId) &&
        String(one.createdBy) === String(userId) &&
        OPEN_STATUSES.includes(one.status)
    })
  }

  // anything this user has offered on, however that offer ended - an offer that
  // lost is withdrawn or declined, so it is no longer one of the open ones
  hasOffered = (lotId) => {
    let userId = this.props.user && this.props.user._id
    return this.state.offers.some(one => {
      return String(one.lot) === String(lotId) && String(one.createdBy) === String(userId)
    })
  }

  awaitingMyAnswer = (lot) => {
    if (lot.sold) {
      return false
    }
    if (!this.isOwnLot(lot)) {
      let myOffer = this.myOffer(lot.id)
      return Boolean(myOffer) && awaitingParty(myOffer) === 'buyer'
    }
    return this.offersReceived(lot.id).some(one => {
      return OPEN_STATUSES.includes(one.status) && awaitingParty(one) === 'seller'
    })
  }


  saleOutcome = (lot) => {
    let userId = this.props.user && this.props.user._id
    if (!lot.sold || !this.props.loggedIn || !userId || this.isOwnLot(lot)) {
      return null
    }
    if (lot.winner && String(lot.winner) === String(userId)) {
      return 'won'
    }
    return this.hasOffered(lot.id) ? 'lost' : null
  }

  // the offers made on a listing of this Seller
  offersReceived = (lotId) => {
    return this.state.offers.filter(one => String(one.lot) === String(lotId))
  }

  isWatched = (lot) => {
    let watchList = (this.props.user && this.props.user.watchList2) || []
    return watchList.some(one => String(one.lotId) === String(lot.id))
  }

  isOwnLot = (lot) => {
    let userId = this.props.user && this.props.user._id
    return Boolean(lot && userId && String(lot.createdBy) === String(userId))
  }

  selectLot = (id) => {
    this.setState({ selectedLotId: id, showDetailsPopup: false, offerUnlocked: false, offerInput: '' })
  }

  // "View in Pop Up" on the lot details, the way the live auction screen opens them
  showDetailsPopup = (value) => {
    this.setState({ showDetailsPopup: value })
  }

  // Closing drops the half-finished offer with the panel, so reopening a lot
  // never comes back with an amount typed for the lot before it.
  closePanel = () => {
    this.setState({
      selectedLotId: null,
      showDetailsPopup: false,
      offerUnlocked: false,
      offerInput: '',
      counterFor: null,
      counterInput: ''
    })
  }

  // A click anywhere that is not the panel itself, another lot card, or a modal
  // on top of the screen closes the panel.
  onDocumentMouseDown = (e) => {
    if (!this.state.selectedLotId) {
      return
    }
    const panel = this.panelRef.current
    if (panel && panel.contains(e.target)) {
      return
    }
    // a click on another card selects that lot, it does not close the panel;
    // modals are drawn over the board and must not close what is behind them.
    // They are drawn outside the panel, and each carries its own portal class -
    // the image preview is `ReactModalPortal-Email` - so any of them counts.
    if (e.target.closest && e.target.closest(
      '.lot-item, .app-modal-overlay, [class*="ReactModalPortal"], .ReactModal__Overlay, .obp-details-popup, .obp-popup-layer'
    )) {
      return
    }
    this.closePanel()
  }

  unlockOffer = () => this.setState({ offerUnlocked: true })

  onOfferInput = (e) => {
    const v = e.target.value
    if (/^\d*\.?\d*$/.test(v)) {
      this.setState({ offerInput: v })
    }
  }

  requestSend = (lot) => {
    const amount = parseFloat(this.state.offerInput)
    if (!amount) {
      return
    }
    this.props.showModal({
      title: 'Send Offer',
      message: `Are you sure you want to send the offer of ${fmt(lot, toCents(lot, amount))}?`,
      buttons: [
        { text: 'OK', onPress: () => this.confirmSend(lot, amount) },
        { text: 'Cancel' }
      ]
    })
  }

  confirmSend = async (lot, amount) => {
    const { offerBoard } = this.state
    this.setState({ offerUnlocked: false, offerInput: '' })
    let res = await Api.postOffer(offerBoard._id, lot.id, toCents(lot, amount))
    if (res && res.offer) {
      this.loadOffers()
    }
  }

  // the Buyer takes his offer back
  removeOffer = async (offerId) => {
    let res = await Api.withdrawOffer(offerId)
    if (res && res.offer) {
      this.loadOffers()
    }
  }

  // the Seller answers an offer made on his listing
  answerOffer = async (offerId, action, cents) => {
    let res = await Api.answerOffer(offerId, action, cents)
    if (res && res.offer) {
      this.setState({ counterFor: null, counterInput: '' })
      await this.loadOffers()
      if (action === 'accept' || action === 'accept-counter') {
        // the listing is sold, it comes off the board
        this.reloadLots()
      }
    }
  }

  startCounter = (offerId) => this.setState({ counterFor: offerId, counterInput: '' })

  onCounterInput = (e) => {
    const v = e.target.value
    if (/^\d*\.?\d*$/.test(v)) {
      this.setState({ counterInput: v })
    }
  }

  sendCounter = (offerId, lot) => {
    const amount = parseFloat(this.state.counterInput)
    if (!amount) {
      return
    }
    this.answerOffer(offerId, 'counter', toCents(lot, amount))
  }

  goBack = () => {
    if (this.props.history) {
      this.props.history.push('/main/upcoming')
    } else {
      window.location.hash = '/main/upcoming'
    }
  }


  renderOfferHistory (offer, viewerParty, variant = '', lot = null) {
    const history = offerHistory(offer)
    if (history.length < 2) {
      return null
    }
    return (
      <div className={`obp-history ${variant}`}>
        {history.map((step, index) => (
          <div
            className={`obp-history-step ${index === history.length - 1 ? 'latest' : ''}`}
            key={`${step.at}-${index}`}
          >
            <span className='obp-history-what'>{step.counter ? 'Counter' : 'Offer'}</span>
            <span className='obp-history-cents'>{fmt(lot, step.cents)}</span>
            <span className='obp-history-who'>{offerPartyLabel(step.party, viewerParty)}</span>
            <span className='obp-history-when'>{step.at ? localTime(step.at, 'D MMM, h.mm A') : ''}</span>
          </div>
        ))}
      </div>
    )
  }

  renderImage (lot) {
    if (lot && lot.image) {
      return <img src={lot.image} className='lot-image' alt='lot' />
    }
    // placeholder when the listing has no media
    return <span className='lot-image no-image'>No photo/video</span>
  }

  // Mirrors RenderLot.renderGridContent markup so the shared App.css styles the
  // card exactly like a live-auction lot - minus the red/green win colouring.
  renderLotCard (lot) {
    const { selectedLotId, gridState } = this.state
    const loggedIn = this.props.loggedIn
    const hideImages = gridState === 'no-image'
    const isSelected = selectedLotId === lot.id
    const myOffer = this.myOffer(lot.id)
    const outcome = this.saleOutcome(lot)
    const awaitingMe = this.awaitingMyAnswer(lot)
    return (
      <div
        key={lot.id}
        className={`lot-item ${isSelected ? 'selected' : ''} ${hideImages ? 'no-img' : ''} ${lot.sold ? 'obp-sold' : ''} ${awaitingMe ? 'obp-awaiting' : ''}`}
        onClick={() => this.selectLot(lot.id)}
      >
        {/* a sale stays on screen for a week, banded across so it is never
            mistaken for a listing still open for offers - and a Buyer who was
            offering on it is told how it went for him rather than just SOLD */}
        {lot.sold && (
          <span className={`obp-sold-banner ${outcome || ''}`}>
            {outcome === 'won' ? 'WON' : outcome === 'lost' ? 'LOST' : 'SOLD'}
          </span>
        )}
        <div className={`lot-header ${this.isWatched(lot) ? 'watched' : ''}`}>
          <div className='left'>
            Lot {lot.number}
            <div className='small-watch-btn'>
              {hideImages && loggedIn
                ? <WatchButton lotId={lot.id} bigButton={false} isBorder={false} />
                : null}
            </div>
          </div>
          <div className='location'>{lot.location}</div>
          {!hideImages && loggedIn
            ? <WatchButton lotId={lot.id} bigButton={false} isBorder />
            : null}
        </div>
        <div className='lot-body'>
          {!hideImages && this.renderImage(lot)}
          <div className={`lot-info ${isSelected ? 'selected' : ''} ${hideImages ? 'no-img' : ''}`}>
            <div className='top'>
              <div className='left'>
                <span className='timer-text'>{lot.sold ? 'SOLD' : 'OPEN'}</span>
                {awaitingMe && <FontAwesomeIcon className='obp-gavel' icon={faGavel} />}
              </div>
              <span className='lot-status'>
                {lot.sold
                  ? (lot.soldPrice || 'SOLD')
                  : typeof lot.daysLeft === 'number' ? `${lot.daysLeft} DAYS LEFT` : 'OPEN'}
              </span>
            </div>
            <div className={`center ${isSelected ? 'selected' : ''} ${hideImages ? 'no-img' : ''}`}>
              <div className='auc-title'>
                <p className='auc-title-main'>{lot.title}</p>
                <p className='auc-title-main-secondary'>{lot.age}</p>
              </div>
              <div className='auc-title-weights'>
                <p className='auc-title-weight'>{lot.assWeight}</p>
                <p className='auc-title-weight curfew'>{lot.curWeight}</p>
              </div>
            </div>
            <div className={`bottom ${isSelected ? 'selected' : ''} ${hideImages ? 'no-img' : ''}`}>
              <div className='price-left' />
              <div className='price-center'>
                <div className='auc-price'>
                  {myOffer && (
                    <span className='obp-card-offer'>
                      {myOffer.status !== 'countered'
                        ? `YOUR OFFER ${fmt(lot, myOffer.cents)}`
                        : awaitingParty(myOffer) === 'buyer'
                          ? `COUNTERED ${fmt(lot, myOffer.counterCents)}`
                          : `YOUR COUNTER ${fmt(lot, myOffer.counterCents)}`}
                    </span>
                  )}
                  {/* once it is sold what it asked no longer matters - the
                      price on the tile is what it went for, labelled so it is
                      not read as the asking price */}
                  {lot.sold && lot.soldPrice ? (
                    <React.Fragment>
                      <span className='obp-card-sold-label'>SOLD AT</span>
                      {lot.soldPrice}{lot.priceMeasure}
                    </React.Fragment>
                  ) : `${lot.price}${lot.priceMeasure}`}
                </div>
              </div>
              <div className='price-right' />
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Mirrors Bidding.js markup so the shared App.css styles the panel identically.
  renderOfferPanel (lot) {
    const { offerUnlocked, offerInput, counterFor, counterInput } = this.state
    const myOffer = this.myOffer(lot.id)
    // the Buyer answers only when the offer is with him, and only one thing at
    // a time - either the answer buttons or the counter he is typing
    const myTurn = Boolean(myOffer) && awaitingParty(myOffer) === 'buyer'
    const myCounterOpen = Boolean(myOffer) && counterFor === myOffer._id

    return (
      <React.Fragment>
        <div className='bidding-container'>
          <div className='content-container'>
            <div className='row header'>
              <span className='header-lot-number'>Lot {lot.number}</span>
              <span className='location'>{lot.location}</span>
            </div>
            <div className='row info'>
              <span className='lot-title'>{lot.title}</span>
              <span className='lot-title'>{lot.age}</span>
              <div className='lot-title-weight'>
                <span className='lot-title'>{lot.assWeight}</span>
                <span className='lot-title'>{lot.curWeight}</span>
              </div>
              <div className='price'>
                <span>{lot.price}</span>
                <span className='price-measure'>{lot.priceMeasure}</span>
              </div>
            </div>
            <div className='overlay-container'>
              <div className='row status'>
                <span className='timer-text'>{lot.sold ? 'SOLD' : 'OFFERS OPEN'}</span>
                <span className='lot-status'>
                  {lot.sold
                    ? (lot.soldAt ? `SOLD ${localTime(lot.soldAt, 'D MMM YYYY')}` : 'SOLD')
                    : lot.expiresAt ? `EXPIRES ${localTime(lot.expiresAt, 'D MMM YYYY')}` : 'OPEN'}
                </span>
              </div>

              {lot.sold ? (
                /* the listing is sold and off the board - nothing left to
                   answer, it is here so both sides keep seeing the sale */
                <div className='row auto-bid active'>
                  <span className='left'>
                    SOLD{lot.soldPrice ? ` AT ${lot.soldPrice}` : ''}
                  </span>
                </div>
              ) : myOffer && myCounterOpen ? (
                /* the Buyer countering the Seller back */
                <div className='row auto-bid active'>
                  <span className='left'>YOUR COUNTER</span>
                  <div className='bid-buttons'>
                    <input
                      type='text'
                      className='bid-input'
                      placeholder={getInputPlaceholder(lot)}
                      value={counterInput}
                      onChange={this.onCounterInput}
                    />
                    <div className='bid-button' onClick={() => this.sendCounter(myOffer._id, lot)}>
                      SEND
                    </div>
                    <div className='bid-button' onClick={() => this.setState({ counterFor: null })}>
                      CANCEL
                    </div>
                  </div>
                </div>
              ) : myOffer && myTurn ? (
                /* the Seller countered - the Buyer answers it, and may counter
                   it in turn, for as many rounds as the two of them want */
                <div className='row auto-bid active'>
                  <span className='left'>
                    SELLER COUNTERED<br />AT {fmt(lot, myOffer.counterCents)}
                  </span>
                  <div className='bid-buttons'>
                    <div className='bid-button' onClick={() => this.answerOffer(myOffer._id, 'accept')}>
                      ACCEPT
                    </div>
                    <div className='bid-button' onClick={() => this.answerOffer(myOffer._id, 'decline')}>
                      DECLINE
                    </div>
                    <div className='bid-button' onClick={() => this.startCounter(myOffer._id)}>
                      COUNTER
                    </div>
                  </div>
                </div>
              ) : myOffer ? (
                /* waiting on the Seller - either the offer as made, or the
                   Buyer's own last counter */
                <div className='row auto-bid active'>
                  <span className='left'>
                    {myOffer.status === 'countered'
                      ? `YOUR COUNTER AT ${fmt(lot, myOffer.counterCents)}`
                      : `OFFER ACTIVE SET AT ${fmt(lot, myOffer.cents)}`}
                    <br />WITH SELLER
                  </span>
                  <div className='bid-buttons'>
                    <div className='bid-button' onClick={() => this.removeOffer(myOffer._id)}>
                      REMOVE
                    </div>
                  </div>
                </div>
              ) : !offerUnlocked ? (
                <div className='lock-bids' onClick={this.unlockOffer}>
                  <FontAwesomeIcon icon={faLock} />
                  <p>CLICK TO MAKE OFFER</p>
                </div>
              ) : (
                <div className='row auto-bid'>
                  <span className='left'>OFFER</span>
                  <div className='bid-buttons'>
                    <input
                      type='text'
                      className='bid-input'
                      placeholder={getInputPlaceholder(lot)}
                      value={offerInput}
                      onChange={this.onOfferInput}
                    />
                    <div className='bid-button' onClick={() => this.requestSend(lot)}>
                      SEND
                    </div>
                  </div>
                </div>
              )}
              {/* how the price got to where it is - the Buyer's own offer and
                  every counter since */}
              {this.renderOfferHistory(myOffer, 'buyer', 'in-panel', lot)}
            </div>
          </div>
        </div>
        {this.props.loggedIn &&
          <div className='watch-container'>
            <WatchButton lotId={lot.id} bigButton />
          </div>}
               <LotInfo key={lot.id} lotId={lot.id} showInPopup={false} showingInPopup={this.showDetailsPopup} />
      </React.Fragment>
    )
  }

  // The Seller's own listing: the offers made on it, with what he can do about
  // each of them.
  renderMyOffersSeller (lot) {
    const { counterFor, counterInput } = this.state
    const offers = this.offersReceived(lot.id)
    return (
      <div className='obp-offers'>
        <div className='obp-offers-head'>
          <span className='obp-offers-title'>Offers Received</span>
          <span className='obp-offers-lot'>Lot {lot.number}</span>
        </div>
        {!offers.length
          ? <p className='obp-empty-note'>No offers yet.</p>
          : offers.map((offer) => {
            const buyer = offer.creatorData || {}
            const name = `${buyer.firstName || ''} ${buyer.lastName || ''}`.trim()
            const countered = offer.status === 'countered'
            const open = OPEN_STATUSES.includes(offer.status)
            // an offer the Seller has countered is with the Buyer until he
            // answers it - the Seller answers again once it comes back
            const myTurn = open && awaitingParty(offer) === 'seller'
            return (
              <div className='obp-offer' key={offer._id}>
                <div className='obp-offer-top'>
                  <div className='obp-offer-who'>
                    <span className='obp-offer-buyer'>{name || 'Buyer'}</span>
                    <span className='obp-offer-time'>{localTime(offer.createdAt, 'D MMM, h.mm A')}</span>
                    {/* what the listing asks, in the same shape the lot tile
                        shows it, so the offer on the right of this row can be
                        read straight against it */}
                    <span className='obp-offer-asked'>Asking {lot.price}{lot.priceMeasure}</span>
                  </div>
                  <div className='obp-offer-amount'>
                    <span className='obp-offer-cents'>{fmt(lot, amountOnTheTable(offer))}</span>
                    {countered &&
                      <span className='obp-offer-note'>
                        {offer.counterParty === 'buyer' ? 'buyer countered' : 'you countered'}
                      </span>}
                  </div>
                </div>
                {/* every round of the negotiation, not just the last counter */}
                {this.renderOfferHistory(offer, 'seller', '', lot)}
                <div className='obp-offer-bottom'>
                  <span className={`obp-offer-status ${offer.status}`}>{offer.status}</span>
                  {open && !myTurn && <span className='obp-offer-waiting'>with buyer</span>}
                </div>
                {/* The Seller answers on the same red row of white buttons the
                    Buyer answers a counter on, so both sides of a negotiation
                    are worked the same way. */}
                {counterFor === offer._id ? (
                  <div className='row auto-bid active'>
                    <span className='left'>YOUR COUNTER</span>
                    <div className='bid-buttons'>
                      <input
                        type='text'
                        className='bid-input'
                        placeholder={getInputPlaceholder(lot)}
                        value={counterInput}
                        onChange={this.onCounterInput}
                      />
                      <div className='bid-button' onClick={() => this.sendCounter(offer._id, lot)}>
                        SEND
                      </div>
                      <div className='bid-button' onClick={() => this.setState({ counterFor: null })}>
                        CANCEL
                      </div>
                    </div>
                  </div>
                ) : myTurn ? (
                  <div className='row auto-bid active'>
                    <span className='left'>
                      {countered && offer.counterParty === 'buyer' ? 'BUYER COUNTERED' : 'OFFER'}
                      <br />AT {fmt(lot, amountOnTheTable(offer))}
                    </span>
                    <div className='bid-buttons'>
                      <div className='bid-button' onClick={() => this.answerOffer(offer._id, 'accept')}>
                        ACCEPT
                      </div>
                      <div className='bid-button' onClick={() => this.answerOffer(offer._id, 'decline')}>
                        DECLINE
                      </div>
                      <div className='bid-button' onClick={() => this.startCounter(offer._id)}>
                        COUNTER
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
      </div>
    )
  }

  renderTab (name, title, icon, borderColor) {
    const active = this.state.tab === name
    return (
      <div
        className={`tab ${title}-tab ${active ? 'active' : ''}`}
        onClick={() => this.setState({ tab: name })}
        style={active ? { borderColor, color: '#ffffff' } : null}
      >
        {icon && <FontAwesomeIcon icon={icon} style={{ color: active ? borderColor : '' }} />}
        {title}
      </div>
    )
  }

  render () {
    const {
      offerBoard, loaded, loadError, tab, gridState, isSound,
      selectedLotId, lots: allLots
    } = this.state
    const user = this.props.user || {}

    // the board has been archived: the screen is left empty, the popup that
    // went up is what tells the user why
    if (this.state.unavailable) {
      return (
        <div className='auction-live live obp-page'>
          <style>{STYLES}</style>
        </div>
      )
    }

    let lots = allLots
    if (tab === 'all') {
      // a sale keeps its place on the board rather than dropping to the back -
      // Lot 4 sells, Lot 4 is still where Lot 4 was
      lots = allLots.concat(this.recentlySold()).sort(byLotNumber)
    } else if (tab === 'watching') {
      lots = allLots.filter(this.isWatched)
    } else if (tab === 'myOffers') {
      // what is still open comes first, the sales of the last week after it, so
      // the newer listings stay at the front
      lots = allLots
        .filter((one) => this.myOffer(one.id) || this.isOwnLot(one))
        .concat(this.mySoldLots())
    } else if (tab === 'sold') {
      lots = this.state.soldLots
    }
    // a sold listing is off the board, so it is only found among the sales
    const selectedLot = allLots.concat(this.state.soldLots).find((one) => one.id === selectedLotId)
    const showSellerOffers = Boolean(selectedLot && this.isOwnLot(selectedLot))

    return (
      <div className='auction-live live obp-page'>
        <style>{STYLES}</style>

        {/* ---- Header (mirrors LiveHeader.js) ---- */}
        <header className='live-app-header'>
          <div className='live-header-wrap'>
            <div className='logo-wrapper' onClick={this.goBack}>
              <div className='logo'><div className='logo-image' /></div>
            </div>
            <div className='data'>
              <div className='title-date'>
                <div className='title'>
                  <p className='auction-number'>{offerBoard ? offerBoard.title : 'Offer Board'}</p>
                </div>
                {offerBoard && <p className='auction-number sub'>Board No. {offerBoard.searchNumber}</p>}
                <div className='date'>{localTime(new Date(), 'dddd D MMMM YYYY, h.mm A')}</div>
              </div>
              <div className='right-data'>
                <div className='live-page-permisson-level-box'>
                  <span>BUYER &amp; SELLER</span>
                </div>
                {user.firstName && (
                  <div className='user-data'>
                    <div className='user-name'>User: {user.firstName} {user.lastName}</div>
                    {user.numericId && <div className='user-id'>ID: {user.numericId}</div>}
                  </div>
                )}
              </div>
            </div>
            <div className='logout-wrapper'>
              <span className='logout' onClick={this.goBack}>
                <FontAwesomeIcon className='icon-door' icon={faDoorOpen} />Leave Board
              </span>
            </div>
          </div>
        </header>

        {/* ---- Nav tabs (mirrors AuctionLive navbar) ---- */}
        <div className='app-navbar live-navbar'>
          <div className='navbar-wrap'>
            <div className='navbar'>
              <div className='navbar-tabs'>
                {this.renderTab('all', 'All', faTh, '#ffffff')}
                {this.renderTab('watching', 'Watching', faBookmark, '#007fc1')}
                {this.renderTab('myOffers', 'My Offers', faTh, '#ffffff')}
                {this.renderTab('sold', 'Sold', faTh, '#3aa856')}
              </div>
              <div className='toggle-pictures'>
                <div className='grid-row'>
                  <div onClick={() => this.setState({ gridState: 'default' })}>
                    <FontAwesomeIcon className={`grid-icon ${gridState === 'default' ? 'active' : ''}`} icon={faThLarge} />
                  </div>
                  <div onClick={() => this.setState({ gridState: 'no-image' })}>
                    <FontAwesomeIcon className={`grid-icon ${gridState === 'no-image' ? 'active' : ''}`} icon={faThList} />
                  </div>
                  <div onClick={() => this.setState({ gridState: 'list' })}>
                    <FontAwesomeIcon className={`grid-icon ${gridState === 'list' ? 'active' : ''}`} icon={faBars} />
                  </div>
                </div>
                <div className='toggle-item'>
                  <p>Off</p>
                  <Toggle icons={false} checked={isSound} onChange={() => this.setState({ isSound: !isSound })} />
                  <p>Sound</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---- Body (mirrors live-auction layout) ---- */}
        <div className='live-auction'>
          <div className='render-lots-container'>
            <div className='live-lots-list'>
              {!loaded
                ? <div className='obp-empty'>Loading the board...</div>
                : loadError
                  ? <div className='obp-empty'>{loadError}</div>
                  : lots.length
                    ? lots.map((lot) => this.renderLotCard(lot))
                    : <div className='obp-empty'>{EMPTY_TAB_NOTE[tab] || EMPTY_TAB_NOTE.all}</div>}
            </div>
          </div>

          {selectedLot && (
            <div className='single-lot-container obp-panel' ref={this.panelRef}>
              <span
                className='obp-panel-close'
                title='Close'
                onClick={this.closePanel}
              >
                <FontAwesomeIcon icon={faTimes} />
              </span>
              {showSellerOffers
                ? (
                  <React.Fragment>
                    {this.renderMyOffersSeller(selectedLot)}
                    <LotInfo key={selectedLot.id} lotId={selectedLot.id} showInPopup={false} showingInPopup={this.showDetailsPopup} />
                  </React.Fragment>
                )
                : this.renderOfferPanel(selectedLot)}
            </div>
          )}

          {selectedLot && this.state.showDetailsPopup && (
            <React.Fragment>
              <div className='obp-popup-layer' onClick={() => this.showDetailsPopup(false)} />
              <div className='single-lot-container details-popup obp-details-popup'>
                <LotInfo key={`popup-${selectedLot.id}`} lotId={selectedLot.id} showInPopup showingInPopup={this.showDetailsPopup} />
              </div>
            </React.Fragment>
          )}
        </div>

      </div>
    )
  }
}

/* The live screen pins its lots list and its offer panel with position:fixed at
   top:180px, an offset that only lines up with its own header. The board is a
   full height flex column instead: header and tabs stay put and only the lots
   list scrolls. Every selector carries .obp-page so nothing here reaches the
   live auction screen. */
const STYLES = `
.obp-page { position:fixed; top:0; right:0; bottom:0; left:0; display:flex; flex-direction:column; overflow:hidden; }
.obp-page .live-app-header, .obp-page .app-navbar { flex:0 0 auto; }
.obp-page .live-auction { flex:1 1 auto; min-height:0; display:flex; align-items:stretch; }
.obp-page .render-lots-container { flex:1 1 auto; min-height:0; display:flex; }
.obp-page.live .render-lots-container .live-lots-list {
  position:static; top:auto; left:auto; width:100%; height:auto;
  flex:1 1 auto; min-height:0; overflow-y:auto; overflow-x:hidden; align-content:flex-start;
}
.obp-page.live .live-auction .single-lot-container {
  position:static; top:auto; right:auto; height:auto;
  flex:0 0 430px; min-height:0; overflow-y:auto; overflow-x:hidden;
}
@media (max-width: 900px) {
  .obp-page { position:static; height:auto; overflow:visible; }
  .obp-page .live-auction { display:block; }
  .obp-page .render-lots-container { display:block; }
  .obp-page.live .render-lots-container .live-lots-list,
  .obp-page.live .live-auction .single-lot-container { overflow:visible; flex:none; width:100%; }
}
/* the live screen marks the selected lot by dropping the card padding, which is
   easy to miss on a board, so the selected tile is outlined as well */
.obp-page.live .lot-item { box-shadow: 0 0 0 2px transparent; transition: box-shadow .15s ease; }
.obp-page.live .lot-item.selected { box-shadow: 0 0 0 3px #007fc1; }
/* The panel is a scrolling flex column, so the close button is a sticky flex
   item pinned to its top right: it stays in the corner while the lot details
   underneath scroll past it. */
.obp-panel-close {
  position:sticky; top:6px; z-index:5;
  align-self:flex-end; flex:0 0 auto; margin:6px 8px 2px 0;
  display:flex; align-items:center; justify-content:center;
  width:28px; height:28px; border-radius:50%;
  background:#fff; box-shadow:0 1px 4px rgba(0,0,0,.3);
  color:#555; cursor:pointer; font-size:15px;
}
.obp-panel-close:hover { background:#c0392b; color:#fff; }
.obp-empty { color:#666; padding:40px; font-family: Lato, 'Myriad Pro', sans-serif; }
.obp-empty-note { color:#888; font-size:12px; margin:0; }
.obp-card-offer { display:block; font-size:12px; color:#c0392b; }
.obp-card-sold-label { display:block; font-size:12px; color:#2e7d32; }
/* the live screen sets Lato on its panels, and form controls do not inherit a
   font at all, so the buttons and the input are told explicitly */
.obp-offers, .obp-offers input, .obp-offers button { font-family: Lato, 'Myriad Pro', sans-serif; }
.obp-offers { background:#fff; border-radius:5px; padding:14px; margin:0 0 5px 5px; }
/* the heading of the offers sits on a black bar, so the listing being answered
   is named clearly at the top of the panel */
.obp-offers-head { display:flex; align-items:baseline; justify-content:space-between; background:#000; border-radius:4px; padding:10px 12px; margin-bottom:10px; }
.obp-offers-title { font-size:17px; font-weight:700; color:#fff; }
.obp-offers-lot { font-size:15px; font-weight:700; color:#fff; }
.obp-offer { border-bottom:1px solid #f2f2f2; padding:10px 0; }
.obp-offer:last-child { border-bottom:none; padding-bottom:0; }
.obp-offer-top { display:flex; align-items:flex-start; justify-content:space-between; }
.obp-offer-who { display:flex; flex-direction:column; }
.obp-offer-buyer { font-size:13px; color:#2a6ebb; font-weight:600; }
.obp-offer-time { font-size:11px; color:#999; margin-top:2px; }
.obp-offer-asked { font-size:11px; color:#666; font-weight:600; margin-top:3px; }
.obp-offer-amount { text-align:right; }
.obp-offer-cents { font-size:18px; font-weight:700; color:#333; display:block; }
.obp-offer-note { font-size:11px; color:#999; display:block; }
.obp-offer-was { text-decoration:line-through; }
.obp-offer-waiting { font-size:11px; color:#999; font-style:italic; }
/* An offer waiting on the Seller, or a counter waiting on the Buyer, flashes the
   listing for whoever has to move, the way the live screen flashes a lot that
   is about to end - same keyframes, already in App.css - and marks it with a
   gavel. It stops the moment they accept, decline or counter. */
.obp-page.live .lot-item.obp-awaiting .lot-body .lot-info .top {
  transition: all 1s ease-in-out;
  animation: blink-from-transparent-to-yellow normal 3s infinite ease-in-out;
}
.obp-gavel { margin-left:7px; font-size:13px; vertical-align:middle; }
/* a sold listing, kept on screen for a week after the sale */
.lot-item.obp-sold { position:relative; }
.lot-item.obp-sold .lot-body, .lot-item.obp-sold .lot-header { opacity:.55; }
.obp-sold-banner {
  position:absolute; top:50%; left:0; right:0; z-index:2;
  transform:translateY(-50%) rotate(-8deg);
  background:rgba(58,168,86,.92); color:#fff;
  font-size:22px; font-weight:700; letter-spacing:6px; text-align:center;
  padding:6px 0; pointer-events:none;
}
/* the same band, in the Buyer's own terms - he took it, or he was outbid on it */
.obp-sold-banner.won { background:rgba(0,127,193,.92); }
.obp-sold-banner.lost { background:rgba(192,57,43,.92); }
.obp-history { margin-top:8px; border-top:1px solid #eee; padding-top:6px; }
/* in the Buyer's offer panel the rows run to the panel edges, they need the
   same breathing room the rows above them have */
.obp-history.in-panel { padding:6px 12px 8px; }
.obp-history-step { display:flex; align-items:baseline; gap:8px; font-size:11px; color:#999; padding:2px 0; }
.obp-history-step.latest { color:#333; font-weight:600; }
.obp-history-what { min-width:52px; text-transform:uppercase; letter-spacing:.3px; }
.obp-history-cents { min-width:70px; }
.obp-history-who { flex:1; text-transform:uppercase; letter-spacing:.3px; }
.obp-history-when { white-space:nowrap; }
.obp-offer-bottom { display:flex; align-items:center; justify-content:space-between; margin-top:8px; }
.obp-offer-status { font-size:10px; letter-spacing:.4px; text-transform:uppercase; border-radius:10px; padding:3px 9px; background:rgba(0,0,0,.07); color:#666; }
.obp-offer-status.pending { background:rgba(224,142,11,.15); color:#b3730a; }
.obp-offer-status.countered { background:rgba(42,110,187,.12); color:#2a6ebb; }
.obp-offer-status.accepted { background:rgba(58,168,86,.15); color:#2e7d32; }
.obp-offer-status.declined, .obp-offer-status.withdrawn { background:rgba(192,57,43,.12); color:#c0392b; }
/* The Seller's answer row, styled exactly as the Buyer's. The live rules are
   scoped to .bidding-container, whose .content-container carries a 360px
   min-height that would stretch every offer in the list, so the same values are
   repeated here against .obp-offers rather than nesting that markup. */
.obp-offers .row { margin:5px 0 0; padding:5px; overflow:hidden; display:flex; align-items:center; }
/* The label is 22px on the live screen. The offers card is narrower than the
   Buyer's panel, so at that size it wrapped over several lines and crowded the
   buttons - it is stepped down here, the rest of the row is unchanged. */
.obp-offers .row.auto-bid {
  color:#ffffff; font-family:Lato; font-size:14px; font-weight:400;
  user-select:none; border-radius:5px; background-color:#2e2e2e;
}
.obp-offers .row.auto-bid.active { background-color:#dd2d2e; }
.obp-offers .row.auto-bid .left { margin:0 5px; display:flex; justify-content:center; word-break:break-word; }
.obp-offers .row.auto-bid .bid-buttons { display:flex; justify-content:flex-end; color:#000000; font-size:13px; flex:4 4; }
.obp-offers .row.auto-bid .bid-buttons .bid-input {
  font-family:Lato; width:63%; height:45px; padding-left:15px;
  border:none; border-radius:5px; background-color:#ffffff;
}
.obp-offers .row.auto-bid .bid-buttons .bid-button {
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  height:45px; border-radius:5px; background-color:#ffffff; cursor:pointer;
  padding:4px; user-select:none; -webkit-tap-highlight-color:transparent;
  min-width:76px; margin-left:5px;
}
.obp-offers .row.auto-bid .bid-buttons .bid-button:hover { background-color:#e2e2e2; }
.obp-empty-note { color:#888; font-size:12px; margin:10px 0 0; }
/* "View in Pop Up": the lot details over a dimmed board, sized as the live
   auction screen sizes its own details popup */
.obp-popup-layer { position:fixed; top:0; right:0; bottom:0; left:0; background-color:rgba(51,51,51,.6); z-index:300; }
.obp-page.live .live-auction .single-lot-container.obp-details-popup {
  position:fixed; top:60px; bottom:20px; right:15%; left:auto; width:70%; height:auto;
  flex:none; z-index:400; overflow:hidden; display:flex; flex-direction:column;
}
.obp-page.live .live-auction .single-lot-container.obp-details-popup .info-container { flex:1 1 auto; min-height:0; overflow-y:auto; }
@media (max-width: 900px) {
  .obp-page.live .live-auction .single-lot-container.obp-details-popup { top:20px; right:10px; left:10px; width:auto; }
}
`

const mapStateToProps = (state) => ({
  user: state.user.data,
  loggedIn: state.user.loggedIn
})

const mapDispatchToProps = (dispatch) => bindActionCreators({ showModal }, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(OfferBoard)
